"use client";

import { Button } from "@hisani/ui";
import { Briefcase, Eye, EyeOff } from "lucide-react";
import {
  useEffect,
  useId,
  useState,
  useSyncExternalStore,
  type FormEvent,
  type ReactNode,
} from "react";
import { useRouter } from "next/navigation";
import React from "react";
import { z } from "zod";
import { localizedPath } from "../../../i18n/paths";
import { ApiClientError, requestApiJson } from "../../api-client";
import { useAuth } from "../../auth-client";
import { PublicJobPostingDetailSchema } from "../hire/hire-contracts";

const AuthActionResponseSchema = z
  .object({
    status: z.string().optional(),
    redirectTo: z.string().optional(),
  })
  .passthrough()
  .nullable();

export function LoginGateway({
  children,
  locale,
  loadingLabel,
  retryLabel,
  unavailableLabel,
}: {
  readonly children: ReactNode;
  readonly locale: string;
  readonly loadingLabel: string;
  readonly retryLabel: string;
  readonly unavailableLabel: string;
}) {
  const auth = useAuth();
  const router = useRouter();

  useEffect(() => {
    if (auth.status !== "authenticated") return;
    const returnTo = new URLSearchParams(window.location.search).get(
      "returnTo",
    );
    const destination = auth.account.profileCompleted
      ? returnTo === null
        ? localizedPath(locale, "/dashboard")
        : localizedPath(locale, safeReturnPath(returnTo))
      : localizedPath(locale, "/onboarding");
    router.replace(destination);
  }, [auth, locale, router]);

  if (auth.status === "unavailable") {
    return (
      <div
        className="mx-auto my-16 flex min-h-96 w-full max-w-5xl flex-col items-center justify-center gap-5 rounded-xl border border-border-subtle bg-surface px-6 text-center shadow-elevation-1"
        role="alert"
      >
        <p className="max-w-reading text-sm leading-6 text-secondary">
          {unavailableLabel}
        </p>
        <Button
          type="button"
          variant="secondary"
          onClick={() => void auth.retry()}
        >
          {retryLabel}
        </Button>
      </div>
    );
  }

  if (auth.status !== "anonymous") {
    return (
      <div
        className="mx-auto my-16 h-96 w-full max-w-5xl animate-pulse rounded-xl bg-subtle"
        aria-label={loadingLabel}
      />
    );
  }

  return children;
}

type AuthView =
  "sign-in" | "sign-up" | "verify" | "otp" | "forgot" | "reset" | "mfa";

export interface AuthMessages {
  readonly signIn: string;
  readonly signUp: string;
  readonly signInTitle: string;
  readonly signInDescription: string;
  readonly signUpTitle: string;
  readonly signUpDescription: string;
  readonly email: string;
  readonly emailPlaceholder: string;
  readonly mobileNumber: string;
  readonly mobilePlaceholder: string;
  readonly emailOption: string;
  readonly mobileOption: string;
  readonly contactMethod: string;
  readonly usePassword: string;
  readonly useCode: string;
  readonly sendSignInCode: string;
  readonly otpTitle: string;
  readonly otpDescription: string;
  readonly verifyEmailTitle: string;
  readonly verifyEmailDescription: string;
  readonly verifyPhoneTitle: string;
  readonly verifyPhoneDescription: string;
  readonly password: string;
  readonly passwordPlaceholder: string;
  readonly showPassword: string;
  readonly hidePassword: string;
  readonly forgotPassword: string;
  readonly passwordRequirements: string;
  readonly signingIn: string;
  readonly creatingAccount: string;
  readonly verificationCode: string;
  readonly verificationCodePlaceholder: string;
  readonly verifyAccount: string;
  readonly verifying: string;
  readonly resendCode: string;
  readonly codeResent: string;
  readonly forgotTitle: string;
  readonly forgotDescription: string;
  readonly sendResetCode: string;
  readonly sendingCode: string;
  readonly resetTitle: string;
  readonly resetDescription: string;
  readonly newPassword: string;
  readonly resetPassword: string;
  readonly resettingPassword: string;
  readonly passwordReset: string;
  readonly mfaTitle: string;
  readonly mfaDescription: string;
  readonly verifySignIn: string;
  readonly backToSignIn: string;
  readonly genericError: string;
  readonly invalidCredentials: string;
  readonly accountExists: string;
  readonly confirmationRequired: string;
  readonly invalidCode: string;
  readonly passwordError: string;
  readonly rateLimited: string;
  readonly providerUnavailable: string;
  readonly hireReturnContext: string;
  readonly hireReturnContextLoading: string;
}

export function AuthPanel({
  locale,
  messages,
}: {
  readonly locale: string;
  readonly messages: AuthMessages;
}) {
  const returnTo = useSyncExternalStore(
    subscribeToLocation,
    readReturnPath,
    defaultReturnPath,
  );
  const emailId = useId();
  const passwordId = useId();
  const phoneId = useId();
  const codeId = useId();
  const signInTabId = useId();
  const signUpTabId = useId();
  const tabPanelId = useId();
  const [view, setView] = useState<AuthView>("sign-in");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [contactType, setContactType] = useState<"EMAIL" | "PHONE">("EMAIL");
  const [passwordSignIn, setPasswordSignIn] = useState(false);
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const hirePostingId = hirePostingIdFromReturnPath(returnTo);
  const [hireReturnContext, setHireReturnContext] = useState<z.infer<
    typeof PublicJobPostingDetailSchema
  > | null>(null);

  useEffect(() => {
    if (hirePostingId === null) return;
    let cancelled = false;
    void requestApiJson(
      `/api/v1/hire/postings/${hirePostingId}`,
      PublicJobPostingDetailSchema,
    )
      .then((posting) => {
        if (!cancelled) setHireReturnContext(posting);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [hirePostingId]);

  const setCurrentView = (next: AuthView) => {
    setPassword("");
    setShowPassword(false);
    setView(next);
    if (next === "sign-in" || next === "sign-up") setPasswordSignIn(false);
    setCode("");
    setError(null);
    setStatus(null);
  };

  const authenticate = async () => {
    const response = await authRequest("/api/v1/auth/sign-in", {
      email,
      password,
      locale,
      returnTo: localizedPath(locale, returnTo),
    });
    if (readString(response, "status") === "MFA_REQUIRED") {
      setCurrentView("mfa");
      return;
    }
    const redirectTo = readString(response, "redirectTo");
    if (redirectTo === null) throw new Error("Missing authentication redirect");
    setPassword("");
    setShowPassword(false);
    window.location.assign(redirectTo);
  };

  const contactPoint = {
    type: contactType,
    value: contactType === "EMAIL" ? email : phone,
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPending(true);
    setError(null);
    setStatus(null);
    try {
      if (view === "sign-in") {
        if (passwordSignIn) {
          await authenticate();
        } else {
          await authRequest("/api/v1/auth/sign-in/code", {
            contactPoint,
            locale,
            returnTo: localizedPath(locale, returnTo),
          });
          setCurrentView("otp");
        }
      } else if (view === "sign-up") {
        await authRequest("/api/v1/auth/sign-up", { contactPoint });
        setCurrentView("verify");
      } else if (view === "verify") {
        const response = await authRequest("/api/v1/auth/sign-up/confirm", {
          contactPoint,
          code,
          locale,
          returnTo: localizedPath(locale, returnTo),
        });
        const redirectTo = readString(response, "redirectTo");
        if (redirectTo === null)
          throw new Error("Missing authentication redirect");
        window.location.assign(redirectTo);
      } else if (view === "otp") {
        const response = await authRequest(
          "/api/v1/auth/sign-in/code/confirm",
          { code },
        );
        const redirectTo = readString(response, "redirectTo");
        if (redirectTo === null)
          throw new Error("Missing authentication redirect");
        window.location.assign(redirectTo);
      } else if (view === "forgot") {
        await authRequest("/api/v1/auth/password/reset", { email });
        setCurrentView("reset");
      } else if (view === "reset") {
        await authRequest("/api/v1/auth/password/reset/confirm", {
          email,
          code,
          password,
        });
        setCurrentView("sign-in");
        setStatus(messages.passwordReset);
      } else {
        const response = await authRequest("/api/v1/auth/mfa", { code });
        const redirectTo = readString(response, "redirectTo");
        if (redirectTo === null)
          throw new Error("Missing authentication redirect");
        window.location.assign(redirectTo);
      }
    } catch (caught: unknown) {
      setError(messageForError(caught, messages));
    } finally {
      setPending(false);
    }
  };

  const resend = async () => {
    setPending(true);
    setError(null);
    try {
      await authRequest("/api/v1/auth/sign-up/resend", { contactPoint });
      setStatus(messages.codeResent);
    } catch (caught: unknown) {
      setError(messageForError(caught, messages));
    } finally {
      setPending(false);
    }
  };

  const isSignIn = view === "sign-in";
  const isSignUp = view === "sign-up";
  const choosesContact = isSignIn || isSignUp;
  const showEmail =
    view === "forgot" || (choosesContact && contactType === "EMAIL");
  const showPhone = choosesContact && contactType === "PHONE";
  const showPasswordField =
    (isSignIn && passwordSignIn && contactType === "EMAIL") || view === "reset";
  const showCode =
    view === "verify" || view === "otp" || view === "reset" || view === "mfa";
  const title =
    view === "sign-in"
      ? messages.signInTitle
      : view === "sign-up"
        ? messages.signUpTitle
        : view === "verify"
          ? contactType === "EMAIL"
            ? messages.verifyEmailTitle
            : messages.verifyPhoneTitle
          : view === "otp"
            ? messages.otpTitle
            : view === "forgot"
              ? messages.forgotTitle
              : view === "reset"
                ? messages.resetTitle
                : messages.mfaTitle;
  const description =
    view === "sign-in"
      ? messages.signInDescription
      : view === "sign-up"
        ? messages.signUpDescription
        : view === "verify"
          ? contactType === "EMAIL"
            ? messages.verifyEmailDescription
            : messages.verifyPhoneDescription
          : view === "otp"
            ? messages.otpDescription
            : view === "forgot"
              ? messages.forgotDescription
              : view === "reset"
                ? messages.resetDescription
                : messages.mfaDescription;
  const submitLabel = pending
    ? isSignIn
      ? passwordSignIn
        ? messages.signingIn
        : messages.sendingCode
      : isSignUp
        ? messages.creatingAccount
        : view === "verify" || view === "otp" || view === "mfa"
          ? messages.verifying
          : view === "forgot"
            ? messages.sendingCode
            : messages.resettingPassword
    : isSignIn
      ? passwordSignIn
        ? messages.signIn
        : messages.sendSignInCode
      : isSignUp
        ? messages.signUp
        : view === "verify"
          ? messages.verifyAccount
          : view === "otp"
            ? messages.verifySignIn
            : view === "forgot"
              ? messages.sendResetCode
              : view === "reset"
                ? messages.resetPassword
                : messages.verifySignIn;

  return (
    <div>
      {(isSignIn || isSignUp) && (
        <div
          className="grid grid-cols-2 rounded-lg border border-border-subtle bg-subtle p-1"
          role="tablist"
          aria-label={messages.signInTitle}
        >
          <button
            id={signInTabId}
            type="button"
            role="tab"
            aria-selected={isSignIn}
            aria-controls={tabPanelId}
            disabled={pending}
            onClick={() => setCurrentView("sign-in")}
            className="min-h-11 rounded-md px-4 text-sm font-bold text-secondary transition-[background-color,color,box-shadow] aria-selected:bg-surface aria-selected:text-primary aria-selected:shadow-elevation-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
          >
            {messages.signIn}
          </button>
          <button
            id={signUpTabId}
            type="button"
            role="tab"
            aria-selected={isSignUp}
            aria-controls={tabPanelId}
            disabled={pending}
            onClick={() => setCurrentView("sign-up")}
            className="min-h-11 rounded-md px-4 text-sm font-bold text-secondary transition-[background-color,color,box-shadow] aria-selected:bg-surface aria-selected:text-primary aria-selected:shadow-elevation-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
          >
            {messages.signUp}
          </button>
        </div>
      )}

      <div
        id={isSignIn || isSignUp ? tabPanelId : undefined}
        role={isSignIn || isSignUp ? "tabpanel" : undefined}
        aria-labelledby={
          isSignIn ? signInTabId : isSignUp ? signUpTabId : undefined
        }
        className={isSignIn || isSignUp ? "min-h-[32rem]" : undefined}
      >
        <div className={isSignIn || isSignUp ? "mt-8" : ""}>
          <h2 className="text-2xl font-bold tracking-[-0.02em] text-primary">
            {title}
          </h2>
          <p className="mt-2 text-sm leading-6 text-secondary">{description}</p>
        </div>

        {(isSignIn || isSignUp) && hirePostingId !== null ? (
          <div className="mt-5 flex gap-3 rounded-lg border border-border-subtle bg-canvas p-4">
            <Briefcase
              className="mt-0.5 size-5 shrink-0 text-brand-ink"
              aria-hidden="true"
            />
            {hireReturnContext === null ? (
              <p className="text-sm leading-5 text-secondary">
                {messages.hireReturnContextLoading}
              </p>
            ) : (
              <div className="grid gap-0.5 text-sm leading-5">
                <p className="text-secondary">{messages.hireReturnContext}</p>
                <strong dir="auto" className="text-primary">
                  {hireReturnContext.title}
                </strong>
                <span dir="auto" className="text-secondary">
                  {hireReturnContext.stableName}
                </span>
              </div>
            )}
          </div>
        ) : null}

        <form
          className="mt-7 space-y-5"
          onSubmit={(event) => void submit(event)}
        >
          {choosesContact ? (
            <fieldset>
              <legend className="text-sm font-semibold text-primary">
                {messages.contactMethod}
              </legend>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {(["EMAIL", "PHONE"] as const).map((type) => (
                  <button
                    key={type}
                    type="button"
                    aria-pressed={contactType === type}
                    disabled={pending}
                    onClick={() => {
                      setContactType(type);
                      setPasswordSignIn(false);
                      setPassword("");
                      setError(null);
                      setStatus(null);
                    }}
                    className="min-h-11 rounded-md border border-border-default px-3 text-sm font-semibold text-secondary aria-pressed:border-brand aria-pressed:bg-brand-subtle aria-pressed:text-brand-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
                  >
                    {type === "EMAIL"
                      ? messages.emailOption
                      : messages.mobileOption}
                  </button>
                ))}
              </div>
            </fieldset>
          ) : null}

          {showEmail && (
            <label className="block" htmlFor={emailId}>
              <span className="text-sm font-semibold text-primary">
                {messages.email}
              </span>
              <input
                id={emailId}
                name="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                required
                disabled={pending}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder={messages.emailPlaceholder}
                className="mt-2 min-h-12 w-full rounded-md border border-border-default bg-surface px-4 text-base text-primary outline-none transition-[border-color,box-shadow] placeholder:text-tertiary focus:border-focus focus:ring-2 focus:ring-focus/20 disabled:bg-[var(--color-surface-sunken)] disabled:text-[var(--color-text-disabled)]"
              />
            </label>
          )}

          {showPhone ? (
            <label className="block" htmlFor={phoneId}>
              <span className="text-sm font-semibold text-primary">
                {messages.mobileNumber}
              </span>
              <input
                id={phoneId}
                name="phone"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                dir="ltr"
                required
                disabled={pending}
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
                placeholder={messages.mobilePlaceholder}
                pattern="\+[1-9][0-9]{7,14}"
                className="mt-2 min-h-12 w-full rounded-md border border-border-default bg-surface px-4 text-base text-primary outline-none transition-[border-color,box-shadow] placeholder:text-tertiary focus:border-focus focus:ring-2 focus:ring-focus/20 disabled:bg-[var(--color-surface-sunken)] disabled:text-[var(--color-text-disabled)]"
              />
            </label>
          ) : null}

          {showPasswordField && (
            <div>
              <label
                className="text-sm font-semibold text-primary"
                htmlFor={passwordId}
              >
                {view === "reset" ? messages.newPassword : messages.password}
              </label>
              <span className="relative mt-2 block">
                <input
                  id={passwordId}
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete={isSignIn ? "current-password" : "new-password"}
                  required
                  disabled={pending}
                  minLength={12}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder={messages.passwordPlaceholder}
                  className="min-h-12 w-full rounded-md border border-border-default bg-surface px-4 pe-12 text-base text-primary outline-none transition-[border-color,box-shadow] placeholder:text-tertiary focus:border-focus focus:ring-2 focus:ring-focus/20 disabled:bg-[var(--color-surface-sunken)] disabled:text-[var(--color-text-disabled)]"
                />
                <button
                  type="button"
                  disabled={pending}
                  onClick={() => setShowPassword((value) => !value)}
                  className="absolute inset-y-0 end-0 flex min-w-12 items-center justify-center text-secondary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus"
                  aria-label={
                    showPassword ? messages.hidePassword : messages.showPassword
                  }
                >
                  {showPassword ? (
                    <EyeOff className="size-5" aria-hidden="true" />
                  ) : (
                    <Eye className="size-5" aria-hidden="true" />
                  )}
                </button>
              </span>
              {view === "reset" && (
                <span className="mt-2 block text-xs leading-5 text-tertiary">
                  {messages.passwordRequirements}
                </span>
              )}
            </div>
          )}

          {showCode && (
            <label className="block" htmlFor={codeId}>
              <span className="text-sm font-semibold text-primary">
                {messages.verificationCode}
              </span>
              <input
                id={codeId}
                name="code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                required
                disabled={pending}
                value={code}
                onChange={(event) =>
                  setCode(event.target.value.replace(/\D/g, ""))
                }
                placeholder={messages.verificationCodePlaceholder}
                className="mt-2 min-h-12 w-full rounded-md border border-border-default bg-surface px-4 font-mono text-lg tracking-[0.25em] text-primary outline-none transition-[border-color,box-shadow] placeholder:tracking-normal placeholder:text-tertiary focus:border-focus focus:ring-2 focus:ring-focus/20 disabled:bg-[var(--color-surface-sunken)] disabled:text-[var(--color-text-disabled)]"
              />
            </label>
          )}

          {isSignIn && contactType === "EMAIL" ? (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={pending}
                className="min-h-11 rounded-md px-2 text-sm font-semibold text-primary underline decoration-brand decoration-2 underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
                onClick={() => {
                  setPasswordSignIn((value) => !value);
                  setPassword("");
                  setError(null);
                }}
              >
                {passwordSignIn ? messages.useCode : messages.usePassword}
              </button>
              {passwordSignIn ? (
                <button
                  type="button"
                  disabled={pending}
                  className="min-h-11 rounded-md px-2 text-sm font-semibold text-primary underline decoration-brand decoration-2 underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
                  onClick={() => setCurrentView("forgot")}
                >
                  {messages.forgotPassword}
                </button>
              ) : null}
            </div>
          ) : null}

          <div aria-live="polite" aria-atomic="true">
            {error !== null && (
              <p className="rounded-md border border-danger/20 bg-danger-subtle px-4 py-3 text-sm font-semibold text-danger">
                {error}
              </p>
            )}
            {status !== null && (
              <p className="rounded-md border border-success/20 bg-success-subtle px-4 py-3 text-sm font-semibold text-success">
                {status}
              </p>
            )}
          </div>

          <Button
            className="w-full"
            variant="accent"
            size="lg"
            type="submit"
            loading={pending}
          >
            {submitLabel}
          </Button>
        </form>

        {view === "verify" && (
          <Button
            type="button"
            variant="ghost"
            className="mt-3 w-full"
            disabled={pending}
            onClick={() => void resend()}
          >
            {messages.resendCode}
          </Button>
        )}
        {!isSignIn && !isSignUp && view !== "mfa" && (
          <Button
            type="button"
            variant="ghost"
            className="mt-3 w-full"
            onClick={() => setCurrentView("sign-in")}
          >
            {messages.backToSignIn}
          </Button>
        )}
      </div>
    </div>
  );
}

function subscribeToLocation(): () => void {
  return () => undefined;
}

function readReturnPath(): string {
  return safeReturnPath(
    new URLSearchParams(window.location.search).get("returnTo"),
  );
}

function defaultReturnPath(): string {
  return "/dashboard";
}

function hirePostingIdFromReturnPath(returnTo: string): string | null {
  const url = new URL(returnTo, "https://hisani.invalid");
  if (url.pathname !== "/hire/posting") return null;
  return url.searchParams.get("postingId");
}

function safeReturnPath(value: string | null): string {
  return value !== null &&
    value.startsWith("/") &&
    !value.startsWith("//") &&
    !value.includes("\\") &&
    !value.includes("\u0000")
    ? value
    : "/dashboard";
}

async function authRequest(
  path: `/api/v1/${string}`,
  body: unknown,
): Promise<unknown> {
  return requestApiJson(path, AuthActionResponseSchema, {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(body),
  });
}

function readString(value: unknown, key: string): string | null {
  if (typeof value !== "object" || value === null) return null;
  const entry = Object.entries(value).find(([entryKey]) => entryKey === key);
  return typeof entry?.[1] === "string" ? entry[1] : null;
}

function messageForError(error: unknown, messages: AuthMessages): string {
  if (!(error instanceof ApiClientError)) return messages.genericError;
  const known: Record<string, string> = {
    AUTH_INVALID_CREDENTIALS: messages.invalidCredentials,
    AUTH_ACCOUNT_EXISTS: messages.accountExists,
    AUTH_CONFIRMATION_REQUIRED: messages.confirmationRequired,
    AUTH_INVALID_CODE: messages.invalidCode,
    AUTH_PASSWORD_REQUIREMENTS: messages.passwordError,
    AUTH_RATE_LIMITED: messages.rateLimited,
    AUTH_PROVIDER_UNAVAILABLE: messages.providerUnavailable,
  };
  return known[error.code] ?? messages.genericError;
}
